import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Modal, ModalProps } from '/imports/ui/Modal';
import { Form } from '/imports/ui/Form';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { useMethod } from '/imports/hooks';
import { modelClass, SolarBill } from '/imports/api/solarBills';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { css } from '/imports/ui/css';
import { Checkbox } from '/imports/ui/Checkbox';
import { toastOnError } from '/imports/ui/utils';
import { getCalpineXlsxUrl, sendCalpineBills } from '/imports/api/calpineBilling';
import { MILLIS_1_WEEK } from '/imports/constants';

const CalpineSendBillsModalStyled = styled(Modal)`
  .modal-window {
    width: 45rem;
    max-width: 100%;
    max-height: 100%;
    .jodit-container {
      border: ${css.inputBorder};
    }
    .test {
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      input {
        position: relative;
        left: -3px;
        top: -4px;
        margin-right: 5px;
      }
    }
  }
`;

interface CalpineSendBillsModalProps extends ModalProps {
  billIds: string[];
}

/** Modal for composing and sending Calpine production-data bill emails. */
export const CalpineSendBillsModal = (props: CalpineSendBillsModalProps) => {
  const { billIds } = props;
  const [result, loading] = useMethod(modelClass.listMethod, { query: { _id: { $in: billIds } }, limit: billIds.length }, [billIds.join(',')]);
  const bills = result?.documents;
  const [subject, setSubject] = useState('New Production Data');
  const [html, setHtml] = useState('');
  const [ccEmails, setCcEmails] = useState<string>();
  const [test, setTest] = useState(true);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!bills?.length) return;
    (async () => {
      for (const bill of bills) {
        const xlsxUrl = await getCalpineXlsxUrl.call({ xlsxPath: bill.extra.xlsxPath, expiresIn: MILLIS_1_WEEK / 1000 });
        bill.extra.xlsxUrl = xlsxUrl;
      }
      setHtml(defaultMessage(bills));
    })();
  }, [bills?.length]);
  useEffect(() => {
  }, [html]);
  const send = toastOnError(async () => {
    setSending(true);
    const num = await sendCalpineBills.call({ billIds, subject, html, ccEmails, test });
    setSending(false);
    toast.success(`${strings.Sent()} ${num}`);
    props.onClose?.();
  });
  return (
    <CalpineSendBillsModalStyled
      {...props}
      title={`${strings.Send()} - Calpine`}
      onClose={props.onClose}
      body={
        loading || sending ?
          <LoadingFlex caption={sending ? strings.Sending() : ''} />
          :
          <div>
            <div>{strings.YouAreSendingNumBills(bills?.length || 0)}</div>
            <hr />
            <Form onSubmit={() => { }}>
              <FormField field="subject" className="checkbox test">
                <label><Checkbox id="cal-test" checked={test} onChange={(checked) => setTest(checked)} />{strings.Test()}</label>
              </FormField>
              <div className="field-group">
                <FormField field="subject">
                  <label>{strings.Subject()}</label>
                  <Input id="cal-subject" value={subject} onChange={(subject) => setSubject(subject || '')} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="html">
                  <label>{strings.Message()}</label>
                  <JoditEditor value={html} onChange={(html) => setHtml(html)} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="ccEmails">
                  <label>{strings.CcEmails()} ({strings.CommaSeparated()})</label>
                  <Input id="cal-cc" value={ccEmails} onChange={(ccEmails) => setCcEmails(ccEmails)} />
                </FormField>
              </div>
              <div className="buttons">
                <Button id="cal-send-btn" onClick={() => send()}>{strings.Send()}</Button>
              </div>
            </Form>
          </div>
      }
    />
  );
};
/**
 * Builds the default Calpine bill email HTML with download links for each bill.
 * @param bills - bills whose xlsx download URLs are embedded
 * @returns the email body HTML
 */
const defaultMessage = (bills: SolarBill[]) => /*html*/`
<div style="max-width: 600px; font-size: 11pt;">
  <p>Hello</p>
  <p>New production data is available for download.</p>
    ${bills.map((b) => /*html*/`<p><a href="${b.extra.xlsxUrl}">Download Latest Data</a></p>`)}
  <p>
    <a href="https://${window.location.host}/calpine/list">View History</a>
  </p>
  <p>Thank You</p>
  <p><b>Luminia Team</b></p>
  <hr />
  <div>
    <img src="https://${window.location.host}/images/logo_dark_193_79.png" style="display: block; max-width: 193px; max-height: 79px;">
  </div>
  <p>
    <div>12707 High Bluff Drive, Suite 115</div>
    <div>San Diego, CA 92130</div>
    <div>P: (858) 866-8777</div>
  </p>
  <p style="font-size: 9pt;">
    NOTICE: This communication and its attachments (if any) have been sent to you from Luminia and contain Privileged, Proprietary and/or Confidential Information. If you are not the intended recipient, you are hereby notified that any disclosure, copying, distribution, or use of the information contained herein (including any reliance thereon) is STRICTLY PROHIBITED. If you are not the intended recipient appearing in the address lines of this communication, please indicate to the sender that you have received this communication in error, and delete the copy you received.
  </p>
</div>
`;
